import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import equal from "fast-deep-equal";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { useThunkDispatch } from "@/redux/dispatcher";
import { set as setProductComparisonSkuList } from "@/redux/modules/value/productComparisonSkuList/productComparisonSkuList.module";
import * as ProductComparisonSkuListSelectors from "@/redux/modules/value/productComparisonSkuList/productComparisonSkuList.selectors";
import { StorageKeys } from "@/utils/enums";

const getLocalStorageSkus = () => {
    return attempt<Array<string>>(() => {
        const localStorageSkus = LocalStorage.getItem(
            StorageKeys.ProductComparisonSkuList
        );

        if (!localStorageSkus) {
            return [];
        } else {
            return JSON.parse(localStorageSkus);
        }
    }, []);
};

const setLocalStorageSkus = (skuList: Array<string> | undefined) => {
    LocalStorage.setItem(
        StorageKeys.ProductComparisonSkuList,
        JSON.stringify(skuList || [])
    );
};

export const useProducts = () => {
    const dispatch = useThunkDispatch();
    const productComparisonSkuList = useSelector(
        ProductComparisonSkuListSelectors.getSkuList
    );

    const [{ data }, getProducts] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            url: `/products`,
            method: "GET"
        },
        { manual: true, autoCancel: false }
    );

    const fetchedSkus = data?.map(({ sku }) => sku);

    useEffect(() => {
        const skuList = getLocalStorageSkus();

        dispatch(setProductComparisonSkuList(skuList));
    }, [dispatch]);

    useEffect(() => {
        if (!productComparisonSkuList?.length) {
            setLocalStorageSkus([]);
            return;
        }

        if (
            isDefined(fetchedSkus) &&
            equal(getLocalStorageSkus(), productComparisonSkuList)
        ) {
            return;
        }

        getProducts({
            params: {
                sku: productComparisonSkuList.join(","),
                fields: ProductFieldSet.ProductTile
            }
        });

        setLocalStorageSkus(productComparisonSkuList);
    }, [fetchedSkus, productComparisonSkuList, getProducts]);

    const remove = (skuToRemove: string) => {
        const filteredSkus = productComparisonSkuList?.filter(
            (sku: string) => sku !== skuToRemove
        );

        dispatch(setProductComparisonSkuList(filteredSkus ?? []));
    };

    const clear = () => {
        dispatch(setProductComparisonSkuList([]));
    };

    const products = useMemo(() => {
        return data?.filter((product) =>
            productComparisonSkuList?.includes(product.sku)
        );
    }, [data, productComparisonSkuList]);

    return {
        products: products,
        remove: remove,
        clear: clear
    };
};
